import React from "react";
import { AiOutlineDown } from "react-icons/ai";
import handleSubmit from "@/app/services/handleSubmit";
import { actualSite, formatName } from "@/app/utils/utils";
import type { lojaInterface } from "@/app/utils/utils";
import InputMask from "@/app/services/InputMask";
import { useRouter } from "next/router";
import { usePathname } from "next/navigation";

function ContactForm({ options }: { options?: string[] }) {
  const router = useRouter();
  const pathname = usePathname();
  const [formKey, setFormKey] = React.useState(Date.now());
  const [currentPathname, setCurrentPathname] = React.useState(pathname || "/");

  // Log inicial
  React.useEffect(() => {
    console.log("ContactForm Mounted:", {
      initialPathname: pathname,
      currentPathname: currentPathname,
      formKey,
    });
  }, []);

  // Atualiza os estados quando a rota muda
  React.useEffect(() => {
    if (currentPathname !== pathname) {
      const newFormKey = Date.now();
      setCurrentPathname(pathname || "/");
      setFormKey(newFormKey);
    }
  }, [pathname, currentPathname]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = urlParams.toString();

    const contactMethodInput = event.currentTarget.querySelector(
      'input[name="contactMethod"]:checked'
    ) as HTMLInputElement;
    const contactMethod = contactMethodInput ? contactMethodInput.value : "";

    const currentPageValue = (pathname || "/").replace(/\//g, "").toUpperCase();
    const carName = currentPageValue.split("?")[0];

    console.log("Form Submission:", {
      pathname,
      currentPathname,
      currentPageValue,
      carName,
      formKey,
      timestamp: new Date().toISOString(),
    });

    handleSubmit(event, {
      params: utmParams,
      consorcio: false,
      registrationNumber: "123",
      team: "Veiculos Novos",
      locationId: 1,
      model: carName,
      page: currentPageValue,
      wpp: false,
    });
  };

  return (
    <section
      key={formKey}
      id="proposta"
      className="flex flex-col z-10 self-start w-fit relative lg:ml-20 max-lg:mx-auto"
    >
      <div className="flex overflow-hidden flex-col self-center max-w-full bg-white rounded-lg shadow-[2px_2px_24px_rgba(0,0,0,0.15)] w-full lg:w-[400px]">
        <form
          className="flex flex-col justify-center px-4 py-2 w-full items-center"
          onSubmit={onSubmit}
        >
          <h2 className="flex flex-col w-fit mx-auto justify-center items-start pt-2 lg:w-full text-2xl leading-tight text-center text-black">
            Solicitar atendimento
          </h2>
          <hr className="flex mb-2.5 bg-red-600 rounded min-h-[3px] max-lg:mx-auto lg:mr-auto w-[60px]" />
          <p className="text-sm leading-5 text-zinc-600">
            Preencha com suas informações e o nosso consultor entrará em
            contato.
          </p>
          <div className="flex flex-col justify-center px-6 pt-2 pb-8 w-full bg-white max-md:px-5 max-lg:max-w-[400px]">
            <div className="flex flex-col w-full text-sm leading-snug text-neutral-600 border bg-stone-300 bg-opacity-0 border-white border-opacity-0 min-h-[40px]">
              <input
                id="nameInput"
                type="text"
                aria-label="Nome"
                className="mt-1 w-full border-b-2 border-neutral-400 min-h-[2px] pl-5 py-2"
                required
                placeholder="Nome*"
              />
            </div>
            <div className="flex flex-col mt-4 w-full text-sm leading-snug text-neutral-600 border bg-stone-300 bg-opacity-0 border-white border-opacity-0 min-h-[40px]">
              <InputMask
                id="cpfInput"
                type="cpf"
                aria-label="CPF"
                className="mt-1 w-full border-b-2 border-neutral-400 min-h-[2px] pl-5 py-2"
                required
                placeholder="CPF*"
              />
            </div>
            <div className="flex flex-col mt-4 w-full text-sm leading-snug text-neutral-600 border bg-stone-300 bg-opacity-0 border-white border-opacity-0 min-h-[40px]">
              <input
                id="emailInput"
                type="email"
                aria-label="E-mail"
                className="mt-1 w-full border-b-2 border-neutral-400 min-h-[2px] pl-5 py-2"
                required
                placeholder="E-mail*"
              />
            </div>
            <div className="flex flex-col mt-4 w-full text-sm leading-snug text-neutral-600 border bg-stone-300 bg-opacity-0 border-white border-opacity-0 min-h-[40px]">
              <InputMask
                id="phoneInput"
                type="tel"
                aria-label="Telefone"
                className="mt-1 w-full border-b-2 border-neutral-400 min-h-[2px] pl-5 py-2"
                required
                placeholder="Telefone*"
              />
            </div>
            <div className="flex justify-between mt-4 w-full items-center text-sm leading-snug text-neutral-600 bg-white rounded border border-stone-500 min-h-[40px] min-w-[200px]">
              <select
                id="modelInput"
                aria-label="Modelo de Interesse"
                className="flex-1 flex shrink -mr-5 justify-between items-center px-3.5 py-1.5 w-full self-stretch my-auto bg-white"
                required
                defaultValue={""}
              >
                <option value="" disabled>
                  Modelo de Interesse*
                </option>
                {options?.map((e) => (
                  <option value={e} key={`option.${e}`}>
                    {e}
                  </option>
                ))}
              </select>
              <AiOutlineDown className="relative mr-2 pointer-events-none" />
            </div>
            <div className="flex justify-between mt-4 w-full items-center text-sm leading-snug text-neutral-600 bg-white rounded border border-stone-500 min-h-[40px] min-w-[200px]">
              <select
                id="dealerInput"
                aria-label="Concessionária"
                className="flex-1 flex shrink -mr-5 justify-between items-center px-3.5 py-1.5 w-full self-stretch my-auto bg-white"
                required
                defaultValue={""}
              >
                <option value="" disabled>
                  Concessionária*
                </option>
                {actualSite().lojas.map((loja: lojaInterface) => (
                  <option key={loja.nome} value={formatName(loja.nome)}>
                    {formatName(loja.nome)}
                  </option>
                ))}
              </select>
              <AiOutlineDown className="relative mr-2 pointer-events-none" />
            </div>
            <div className="flex gap-2 items-center mt-4 w-full">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="privacyPolicy"
                  required
                  className=""
                />
              </label>
              <span className="flex-1 shrink self-stretch my-auto text-xs leading-4 basis-[11px] text-zinc-600">
                Ao enviar, você concorda com nosso contato e o uso de dados
                conforme nossa Política de Privacidade.
              </span>
            </div>
            <button
              type="submit"
              className="flex-1 mt-8 flex shrink gap-2 justify-center items-center self-stretch px-6 py-3.5 my-auto w-full bg-red-600 rounded min-h-[55px] max-md:px-5"
            >
              Enviar
            </button>
          </div>
        </form>
        <div className="flex w-full min-h-[40px] max-md:max-w-full" />
      </div>
    </section>
  );
}

// Não precisamos mais do wrapper, pois o componente já se remonta automaticamente
export default ContactForm;
